import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { PlatformConnectionRead, PlatformName } from '../../types/api';
import { listConnections } from './integrationsApi';

export interface ConnectionsResult {
  /** One row per platform, in the server's order. Null until the first load. */
  connections: PlatformConnectionRead[] | null;
  /** The LinkedIn row. */
  linkedin: PlatformConnectionRead | null;
  /** The Facebook row (a Page). */
  facebook: PlatformConnectionRead | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedConnections {
  businessId: string;
  connections: PlatformConnectionRead[] | null;
  error: string | null;
}

function findPlatform(
  connections: PlatformConnectionRead[] | null,
  platform: PlatformName,
): PlatformConnectionRead | null {
  return connections?.find((entry) => entry.platform === platform) ?? null;
}

/**
 * The platforms this business can publish to.
 *
 * Same shape as the rest of the hand-rolled reads here: the load result is
 * stored tagged with its `business_id` and the visible state is derived during
 * render, so one account's connection can never flash under another's heading.
 *
 * `businessId` may be null — the review screen only asks when the request it is
 * showing actually targets a platform we publish to, so nothing is fetched for
 * the runs that would ignore the answer.
 */
export function useConnections(businessId: string | null): ConnectionsResult {
  const [loaded, setLoaded] = useState<LoadedConnections | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const connections = await listConnections(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, connections, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          connections: null,
          error: getErrorMessage(caught, 'Could not read your connected accounts.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const isCurrent = loaded !== null && loaded.businessId === businessId;
  const connections = isCurrent ? loaded.connections : null;

  return {
    connections,
    linkedin: findPlatform(connections, 'linkedin'),
    facebook: findPlatform(connections, 'facebook'),
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    refresh,
  };
}
